import React, { FC, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useEngineInstance } from '@surveycake/react-logic-engine';
import {
  Typography,
  Message,
  Icon,
  loadingOutlined,
  exclamationTriangle,
} from '@surveycake/rc';
import { useSurveyConfig } from 'app/core/survey-config';
import { Button, Modal } from 'app/shared/components';
import { storeTempAnswers } from 'app/core/http';
import { domain } from 'config/system';
import { useStyles } from './styles';

const SaveTempAnswer: FC = () => {
  const { wording, surveyHash } = useSurveyConfig();
  const { server } = domain;
  const engine = useEngineInstance();
  const [isOpen, setModalOpen] = useState(false);
  const [tempAnswerHash, setTempAnswerHash] = useState('');
  const [loading, setLoading] = useState(false);
  const styles = useStyles([loading]);

  const tempAnswerURL = `${server}/s/${surveyHash}/a/${tempAnswerHash}`;

  const handleOnClick = async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    const tempAnswers = engine.getTemporaryAnswers();
    const postAnswer = engine.getPostAnswer();

    if (!postAnswer) {
      setLoading(false);
      return;
    }

    try {
      const postData = postAnswer.post;
      const hash = await storeTempAnswers(tempAnswers, postData);
      setLoading(false);
      setTempAnswerHash(hash);
      setModalOpen(true);
    } catch (error) {
      Message.error(wording.module.temp_answer.modal.try_again_later, {
        duration: 2000
      });
      setLoading(false);
    }
  };

  const { title = '' } = engine.getSurveyData();
  const mailTarget = 'mailto:';
  const mailSubject = `subject=${wording.module.temp_answer.email.subject}`;
  const originBody = `${wording.module.temp_answer.email.body.replace('{{subjectTitle}}', title)}\n${tempAnswerURL}`;
  const encodedBody = encodeURIComponent(originBody).replace('%26', '%2526');
  const mailBody = `body=${encodedBody}`;
  const href = `${mailTarget}?${mailSubject}&${mailBody}`;

  return (
    <>
      <div className={styles.tempSaveButton} onClick={handleOnClick}>
        <svg
          viewBox='0 0 24 24'
          width='24'
          height='24'
          fill='currentColor'
          className='save-icon'
        >
          <path d='M19.967 7.217l-3.184-3.184a1.821 1.821 0 0 0-1.287-.533H5.32A1.821 1.821 0 0 0 3.5 5.321V18.68c0 1.006.815 1.821 1.821 1.821H18.68a1.821 1.821 0 0 0 1.821-1.821V8.504c0-.483-.192-.946-.533-1.287zm-5.538-2.503v3.643H7.143V4.714h7.286zm4.857 13.965a.608.608 0 0 1-.607.607H5.32a.608.608 0 0 1-.607-.607V5.32c0-.334.273-.607.607-.607h.608v3.947c0 .503.407.91.91.91h7.893a.91.91 0 0 0 .91-.91V4.733a.603.603 0 0 1 .283.16l3.183 3.182a.603.603 0 0 1 .178.43v10.174zM12 11.089a3.343 3.343 0 0 0-3.34 3.34A3.343 3.343 0 0 0 12 17.768a3.343 3.343 0 0 0 3.34-3.34A3.343 3.343 0 0 0 12 11.09zm0 5.465a2.127 2.127 0 0 1-2.125-2.125c0-1.172.953-2.125 2.125-2.125 1.172 0 2.125.953 2.125 2.125A2.127 2.127 0 0 1 12 16.554z'/>
        </svg>
        <span className='save-label'>
          {wording.common.action.save_temp_answer}
        </span>
        { loading &&
          <span className={styles.spinContainer}>
            <Icon icon={loadingOutlined} spin />
          </span>
        }
      </div>
      { isOpen &&
        <Modal
          className={styles.modal}
          onOpen={() => {
            document.body.style.overflowY = 'hidden';
          }}
          onClose={() => {
            document.body.style.overflowY = '';
          }}
        >
          <header className={styles.header}>
            <Typography variant='h6' color='text.white'>
              {wording.module.temp_answer.modal.title}
            </Typography>
            <div
              className={styles.cross}
              onClick={() => setModalOpen(false)}
            ></div>
          </header>
          <div className={styles.content}>
            <p>{wording.module.temp_answer.modal.content}</p>
            <div className={styles.urlContainer}>
              <input
                type='text'
                readOnly
                value={tempAnswerURL}
              />
              <CopyToClipboard
                text={tempAnswerURL}
                onCopy={() => {
                  Message.success(wording.module.temp_answer.modal.copy_success, {
                    duration: 2000
                  });
                }}
              >
                <Button
                  className='copy-btn'
                  color='primary'
                >
                  {wording.module.temp_answer.modal.copy}
                </Button>
              </CopyToClipboard>
            </div>
            <div className={styles.warning}>
              <Icon icon={exclamationTriangle} />
              {wording.module.temp_answer.modal.warning}
            </div>
          </div>
          <footer className={styles.footer}>
            <a href={href}>
              {wording.module.temp_answer.modal.submit}
            </a>
          </footer>
        </Modal>
      }
    </>
  );
};

export default SaveTempAnswer;
